import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../api';
import WebApp from '@twa-dev/sdk'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faTimes, faEllipsis, faInfoCircle, faCheck, faXmark, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { Dropdown, DropdownDivider, DropdownItem } from 'flowbite-react';
import { useMainButton } from '../hooks/useMainButton';
import { useBackButton } from '../hooks/useBackButton';
import { useDebounce } from '../hooks/useDebounce';

function TopicsDropdown({ topic, messageId, onRename, onEditDescription, onSetEntryPoint, onDownloadReport, onDelete }) {
    return (
        <Dropdown label="" dismissOnClick={true} className="rounded-xl shadow-md focus:outline-none border-none outline-none bg-tg-secondary-bg" outline="none" style={{
            outline: 'none'}} renderTrigger={() => <FontAwesomeIcon icon={faEllipsis} className="text-tg-text" />}>
        <DropdownItem className='text-tg-text' style={{ outline: 'none'}} onClick={() => onRename(topic)}>Переименовать</DropdownItem>
        <DropdownItem className='text-tg-text' style={{ outline: 'none'}} onClick={() => onEditDescription(topic)}>Редактировать описание</DropdownItem>
        <DropdownItem className='text-tg-text' style={{ outline: 'none'}} onClick={() => onSetEntryPoint(topic)}>Точка входа</DropdownItem>
        <DropdownDivider />
        <DropdownItem className='text-tg-text' style={{ outline: 'none'}} onClick={() => onDownloadReport(topic)}>Скачать отчет</DropdownItem>
        <DropdownDivider />
        <DropdownItem style={{ color: 'red', outline: 'none'}} onClick={() => onDelete(topic)}>Удалить</DropdownItem>
      </Dropdown>
    );
}

const TopicsPage = ({ messageId }) => {
    const [topics, setTopics] = useState([]);
    const [newTopicName, setNewTopicName] = useState('');
    const [newTopicDescription, setNewTopicDescription] = useState('');
    const [isCreatingTopic, setIsCreatingTopic] = useState(false);
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [showDescription, setShowDescription] = useState(false);
    const searchInputRef = useRef(null);
    const creatingTopicInputRef = useRef(null);
    const [isSearchVisible, setIsSearchVisible] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const debouncedSearchQuery = useDebounce(searchQuery, 300);
    const [isFetching, setIsFetching] = useState(true);
    const [fetchError, setFetchError] = useState(false);

    // Inline editing state — only one edit at a time
    const [editingTopicId, setEditingTopicId] = useState(null);
    const [editField, setEditField] = useState(null); // 'name' | 'description' | null
    const [editValue, setEditValue] = useState('');
    const editInputRef = useRef(null);

    const isEditing = editingTopicId !== null;
    const hasFormOpen = isCreatingTopic || isEditing;

    const handleMainButtonClick = useCallback(() => {
        if (isCreatingTopic) {
            // Submit the create form programmatically
            const form = document.querySelector('form');
            if (form) form.requestSubmit();
        } else {
            setIsCreatingTopic(true);
        }
    }, [isCreatingTopic]);

    const handleBackClick = useCallback(() => {
        if (isEditing) {
            if (editValue.trim()) {
                WebApp.showConfirm('Отменить редактирование?', (confirmed) => {
                    if (confirmed) {
                        setEditingTopicId(null);
                        setEditField(null);
                        setEditValue('');
                    }
                });
            } else {
                setEditingTopicId(null);
                setEditField(null);
                setEditValue('');
            }
        } else if (isCreatingTopic) {
            if (newTopicName.trim() || newTopicDescription.trim()) {
                WebApp.showConfirm('Отменить создание темы?', (confirmed) => {
                    if (confirmed) {
                        setNewTopicName('');
                        setNewTopicDescription('');
                        setIsCreatingTopic(false);
                    }
                });
            } else {
                setNewTopicName('');
                setNewTopicDescription('');
                setIsCreatingTopic(false);
            }
        }
    }, [isEditing, isCreatingTopic, editValue, newTopicName, newTopicDescription]);

    useMainButton({
        text: isCreatingTopic ? 'Создать' : 'Создать тему',
        onClick: handleMainButtonClick,
        visible: !isEditing,
    });

    useBackButton({
        onClick: handleBackClick,
        visible: hasFormOpen,
    });

    useEffect(() => {
        if (hasFormOpen) {
            WebApp.enableClosingConfirmation();
        } else {
            WebApp.disableClosingConfirmation();
        }
        return () => WebApp.disableClosingConfirmation();
    }, [hasFormOpen]);

    const clearEdit = () => {
        setEditingTopicId(null);
        setEditField(null);
        setEditValue('');
    };

    const startEdit = (topic, field) => {
        setEditingTopicId(topic.topic_id);
        setEditField(field);
        setEditValue(field === 'name' ? topic.name : (topic.description || ''));
    };

    useEffect(() => {
        if (editingTopicId && editInputRef.current) {
            editInputRef.current.focus();
        }
    }, [editingTopicId, editField]);

    const handleSearchIconClick = () => {
        setIsSearchVisible(!isSearchVisible);
    };

    const handleSearchCloseClick = () => {
        setIsSearchVisible(false);
        setSearchQuery('');
    };

    useEffect(() => {
        fetchTopics();
    }, []);

    useEffect(() => {
        if (isSearchVisible && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [isSearchVisible]);

    useEffect(() => {
        if (isCreatingTopic && creatingTopicInputRef.current) {
            creatingTopicInputRef.current.focus();
        }
    }, [isCreatingTopic]);

    useEffect(() => {
        if (debouncedSearchQuery) {
            api.get(`/api/topics/search/${messageId}`, { params: { query: debouncedSearchQuery } })
                .then(response => {
                    setTopics(response.data);
                });
        } else {
            fetchTopics()
        }
    }, [debouncedSearchQuery, messageId]);

    const toggleDescription = (topic) => {
        if (selectedTopic === topic.topic_id) {
            setSelectedTopic(null);
            setShowDescription(false);
        } else {
            setSelectedTopic(topic.topic_id);
            setShowDescription(true);
        }
    }

    const fetchTopics = async () => {
        setIsFetching(true);
        setFetchError(false);
        try {
            const response = await api.get(`/api/topics/all/${messageId}`);
            setTopics(response.data);
        } catch (error) {
            setFetchError(true);
        } finally {
            setIsFetching(false);
        }
    };

    const handleTopicClick = async (topicId) => {
        WebApp.HapticFeedback.impactOccurred('light');
        await api.post('/api/topics/update_message_topic', {
            message_id: messageId,
            topic_id: topicId
        });
        WebApp.HapticFeedback.notificationOccurred('success');
        fetchTopics();
        WebApp.close();
    };

    const handleNewTopicSubmit = async (e) => {
        e.preventDefault();
        const trimmedTopicName = newTopicName.trim();
        const trimmedDescription = newTopicDescription.trim();

        if (trimmedTopicName.length < 1 || trimmedTopicName.length > 70) {
            WebApp.showAlert('Название темы должно содержать от 1 до 70 символов.');
            return;
        }

        if (trimmedDescription.length > 500) {
            WebApp.showAlert('Описание темы не должно превышать 500 символов.');
            return;
        }

        await api.post('/api/topics/create', {
            name: trimmedTopicName,
            description: trimmedDescription || undefined,
            message_id: messageId
        });
        WebApp.HapticFeedback.notificationOccurred('success');
        setNewTopicName('');
        setNewTopicDescription('');
        setIsCreatingTopic(false);
        fetchTopics();
    };

    const handleEditSubmit = async (topic) => {
        const trimmedValue = editValue.trim();
        if (editField === 'name') {
            if (trimmedValue.length < 1 || trimmedValue.length > 70) {
                WebApp.showAlert('Название темы должно содержать от 1 до 70 символов.');
                return;
            }
            try {
                await api.post(`/api/topics/edit`, {
                    topic_id: topic.topic_id,
                    message_id: messageId,
                    topic_name: trimmedValue
                });
                clearEdit();
                fetchTopics();
            } catch (error) {
                WebApp.showAlert('Произошла ошибка при переименовании темы.');
            }
        } else if (editField === 'description') {
            if (trimmedValue.length > 500) {
                WebApp.showAlert('Описание темы не должно превышать 500 символов.');
                return;
            }
            try {
                await api.post(`/api/topics/edit`, {
                    topic_id: topic.topic_id,
                    message_id: messageId,
                    topic_description: trimmedValue
                });
                clearEdit();
                fetchTopics();
            } catch (error) {
                WebApp.showAlert('Произошла ошибка при изменении описания темы.');
            }
        }
    };

    const renameTopic = (topic) => {
        startEdit(topic, 'name');
    };

    const editDescription = (topic) => {
        startEdit(topic, 'description');
    };

    const setMessageAsTopicStart = async (topic) => {
        WebApp.showConfirm(`Установить точку входа для темы «${topic.name}»?`, async (confirmed) => {
            if (!confirmed) return;
            try {
                await api.post(`/api/topics/edit`, {
                    topic_id: topic.topic_id,
                    message_id: messageId,
                    set_as_entry_message: true
                });
                handleTopicClick(topic.topic_id);
            } catch (error) {
                WebApp.showAlert('Произошла ошибка при установке точки входа.');
            }
        });
    };

    const downloadTopicReport = async (topic) => {
        try {
            const response = await api.get(`/api/report/generate_by_topic`, { params: { topic_id: topic.topic_id } });

            if (response.data === true) {
                WebApp.showAlert('Отчет успешно отправлен в Telegram');
            }
        } catch (error) {
            console.error('Error generating topic report:', error);
            if (error.response && error.response.status === 404) {
                WebApp.showAlert('Тема не найдена или нет данных для генерации отчета');
            } else if (error.response && error.response.status === 403) {
                WebApp.showAlert('Доступ запрещен');
            } else {
                WebApp.showAlert('Произошла ошибка при генерации отчета');
            }
        }
    };

    const deleteTopic = async (topic) => {
        WebApp.showConfirm(`Удалить тему «${topic.name}»?`, async (confirmed) => {
            if (!confirmed) return;
            try {
                await api.delete(`/api/topics/${topic.topic_id}`);
                fetchTopics();
            } catch (error) {
                WebApp.showAlert('Произошла ошибка при удалении темы.');
            }
        });
    };

    if (isFetching && topics.length === 0) {
        return (
            <div className="flex justify-center items-center h-screen">
                <FontAwesomeIcon icon={faSpinner} spin className="text-tg-hint text-2xl" />
            </div>
        );
    }

    if (fetchError && topics.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-screen gap-4">
                <p className="text-tg-hint">Не удалось загрузить темы</p>
                <button onClick={fetchTopics} className="py-2 px-4 rounded-lg bg-tg-button text-tg-button-text border-none cursor-pointer">
                    Повторить
                </button>
            </div>
        );
    }

    return (
        <div>
            <div className="pt-5 px-10 text-right">
                {isSearchVisible ? (
                    <div>
                        <input
                            ref={searchInputRef}
                            type="text"
                            placeholder="Поиск"
                            className="outline-none bg-tg-bg text-tg-text border-tg-secondary-bg"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                        />
                        <FontAwesomeIcon icon={faTimes} onClick={handleSearchCloseClick} className="text-tg-text" />
                    </div>
                ) : (
                    <FontAwesomeIcon icon={faMagnifyingGlass} onClick={handleSearchIconClick} className="text-tg-text" />
                )}
            </div>
            <div className="px-4">
                {topics.length === 0 && !isFetching && (
                    <p className="text-center text-tg-hint py-8">Нет тем</p>
                )}
                {topics.map(topic => (
                    <div key={topic.topic_id}>
                        <div
                            className="flex items-center justify-between rounded-xl shadow-lg p-6 my-4 cursor-pointer bg-tg-secondary-bg text-tg-text font-bold"
                            style={{
                                border: topic.is_current ? `2px solid var(--tg-theme-button-color)` : 'none'
                            }}
                        >
                            {editingTopicId === topic.topic_id && editField === 'name' ? (
                                <div className="flex items-center flex-grow gap-2">
                                    <input
                                        ref={editInputRef}
                                        type="text"
                                        value={editValue}
                                        onChange={e => setEditValue(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') handleEditSubmit(topic);
                                            if (e.key === 'Escape') clearEdit();
                                        }}
                                        className="flex-grow outline-none bg-tg-bg text-tg-text border-b border-tg-secondary-bg font-bold"
                                    />
                                    <button onClick={() => handleEditSubmit(topic)} className="text-tg-button bg-transparent border-none">
                                        <FontAwesomeIcon icon={faCheck} />
                                    </button>
                                    <button onClick={clearEdit} className="text-tg-hint bg-transparent border-none">
                                        <FontAwesomeIcon icon={faXmark} />
                                    </button>
                                </div>
                            ) : (
                                <div className="flex items-center flex-grow" onClick={() => handleTopicClick(topic.topic_id)}>
                                    {topic.name}
                                    {topic.is_current && (
                                        <span className="ml-2 px-2 py-1 text-xs rounded-full bg-tg-button text-tg-button-text">
                                            Текущая
                                        </span>
                                    )}
                                </div>
                            )}

                            {topic.description && editingTopicId !== topic.topic_id && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleDescription(topic);
                                    }}
                                    className="mx-2 text-tg-link bg-transparent border-none"
                                >
                                    <FontAwesomeIcon icon={faInfoCircle} />
                                </button>
                            )}

                            {editingTopicId !== topic.topic_id && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                    }}
                                    className="text-tg-text bg-transparent border-none"
                                >
                                    <TopicsDropdown
                                        topic={topic}
                                        messageId={messageId}
                                        onRename={renameTopic}
                                        onEditDescription={editDescription}
                                        onSetEntryPoint={setMessageAsTopicStart}
                                        onDownloadReport={downloadTopicReport}
                                        onDelete={deleteTopic}
                                    />
                                </button>
                            )}
                        </div>

                        {/* Inline description edit */}
                        {editingTopicId === topic.topic_id && editField === 'description' && (
                            <div className="px-4 mb-4 -mt-3">
                                <textarea
                                    ref={editInputRef}
                                    value={editValue}
                                    onChange={e => setEditValue(e.target.value)}
                                    onKeyDown={e => {
                                        if (e.key === 'Escape') clearEdit();
                                    }}
                                    className="w-full outline-none bg-tg-secondary-bg text-tg-text border-tg-secondary-bg rounded-lg p-3 text-sm min-h-[80px] resize-y"
                                    placeholder="Описание темы"
                                />
                                <div className="flex gap-2 mt-2">
                                    <button onClick={() => handleEditSubmit(topic)} className="font-bold py-1.5 px-4 rounded-lg border-none outline-none cursor-pointer bg-tg-button text-tg-button-text text-sm">
                                        Сохранить
                                    </button>
                                    <button onClick={clearEdit} className="font-bold py-1.5 px-4 rounded-lg border-none outline-none cursor-pointer bg-tg-secondary-bg text-tg-text text-sm">
                                        Отмена
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Show description (read-only) */}
                        {selectedTopic === topic.topic_id && showDescription && topic.description && editingTopicId !== topic.topic_id && (
                            <div className="px-6 py-3 mb-4 rounded-lg -mt-3 bg-tg-secondary-bg text-tg-text text-sm font-normal whitespace-pre-line">
                                {topic.description}
                            </div>
                        )}
                    </div>
                ))}
                {isCreatingTopic ? (
                    <form onSubmit={handleNewTopicSubmit} className="mt-4">
                        <input
                            ref={creatingTopicInputRef}
                            type="text"
                            value={newTopicName}
                            onChange={e => setNewTopicName(e.target.value)}
                            placeholder="Название темы"
                            className="border rounded-lg p-1 w-full outline-none bg-tg-bg text-tg-text border-tg-secondary-bg mb-2.5"
                        />
                        <textarea
                            value={newTopicDescription}
                            onChange={e => setNewTopicDescription(e.target.value)}
                            placeholder="Описание темы (необязательно)"
                            className="border rounded-lg p-1 w-full outline-none bg-tg-bg text-tg-text border-tg-secondary-bg mb-2.5 min-h-[80px] resize-y"
                        />
                        <button
                            type="submit"
                            className="font-bold py-2 px-4 rounded-lg border-none outline-none cursor-pointer w-full max-w-[600px] bg-tg-button text-tg-button-text">
                                Создать
                        </button>
                    </form>
                ) : (
                    <button onClick={() => setIsCreatingTopic(true)}
                        className="font-bold py-2 px-4 rounded-lg border-none outline-none cursor-pointer w-full max-w-[600px] bg-tg-button text-tg-button-text">
                            Создать тему
                    </button>
                )}
            </div>
        </div>
    );
};

export default TopicsPage;
